import React, { useState, useEffect } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Box, Button, Popover } from '@mui/material';
import dayjs from 'dayjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
} from 'chart.js';
import billingService from '../../api/billingService';
import customerService from '../../api/customerService';
import { formatCurrency, formatDate } from '../../utils/formatters';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Title,
  Tooltip,
  Legend
);

const BillingDashboard = () => {
  const [data, setData] = useState(null);
  const [members, setMembers] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [assignTo, setAssignTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD')
  });
  const [activeTab, setActiveTab] = useState('subscriptions');
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    const initialize = async () => {
      try {
        const sessionRes = await customerService.getSession();
        if (sessionRes && sessionRes.success) {
          const userInfo = sessionRes.userInfo;
          setUserRole(userInfo.role);
          if (userInfo.role !== 'owner') {
            setAssignTo(userInfo.email);
          }

          const membersRes = await customerService.getAllMembers(userInfo.id);
          if (membersRes?.data?.ActiveMembers) {
            setMembers(membersRes.data.ActiveMembers);
          }
        }
      } catch (error) {
        console.error('Error checking role:', error);
      }
    };
    initialize();
  }, []);

  
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const res = await billingService.getDashboardMetrics({
        ...dateRange,
        ...(assignTo ? { assignTo } : {})
      });
      if (res.success) setData(res.data);
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, userRole, assignTo]);

  /* 
  Removed role restriction to allow all roles to see dashboard.
  Non-owners will only see their assigned data.
  */

  if (loading && !data) {
    return (
      <div className="container-fluid py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  const trendMonths = [];
  for (let i = 11; i >= 0; i--) {
    const d = dayjs().subtract(i, 'month');
    trendMonths.push(d.format('YYYY-MM'));
  }
  const trendMap = (data?.analytics?.revenue_trend_12m || []).reduce((acc, r) => {
    acc[r.month] = r;
    return acc;
  }, {});
  const trendChartData = {
    labels: trendMonths.map(m => dayjs(m + '-01').format('MMM YY')),
    datasets: [
      {
        label: 'Net Revenue',
        data: trendMonths.map(m => trendMap[m]?.revenue ?? 0),
        borderColor: '#0d6efd',
        backgroundColor: 'rgba(13,110,253,0.12)',
        borderWidth: 2.5,
        fill: true,
        tension: 0.35,
        pointRadius: 3.5,
        pointHoverRadius: 5.5,
        pointBackgroundColor: '#0d6efd',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
      }
    ]
  };

  const renderActivityTable = () => {
    const activities = data?.activities?.[activeTab] || [];
    if (activities.length === 0) return <div className="text-center py-4 text-muted small">No recent activity found.</div>;

    return (
      <div className="table-responsive">
        <table className="table table-hover align-middle mb-0">
          <thead className="bg-light">
            <tr>
              <th>Customer / Details</th>
              <th>Amount</th>
              <th>Date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {activities.map((item, idx) => {
              let title = '';
              let subtitle = '';
              let amount = 0;
              let date = '';
              let status = '';
              let statusClass = 'secondary';

              if (activeTab === 'subscriptions' || activeTab === 'renewals') {
                const cust = item.customer || item.customerData;
                title = cust ? `${cust.firstname} ${cust.lastname}` : 'Unknown Customer';
                subtitle = item.custom_alias || (item.productData?.productName || 'No Alias');
                amount = item.amount;
                date = activeTab === 'renewals' ? item.next_renewal_date : item.created_at;
                status = item.status;
                statusClass = status === 'active' ? 'success' : status === 'expired' ? 'danger' : 'warning';
              } else if (activeTab === 'payments') {
                const cust = item.customer || item.customerData;
                title = cust ? `${cust.firstname} ${cust.lastname}` : 'Unknown Customer';
                subtitle = `Ref: ${item.transaction_number || item.transaction_id || item.reference_number || 'N/A'}`;
                amount = item.amount;
                date = item.transaction_date || item.paymentDate;
                status = item.status || (item.payment_type === 'credit' ? 'received' : 'refund');
                statusClass = status === 'success' || status === 'received' ? 'success' : 'danger';
              } else if (activeTab === 'quotations') {
                const cust = item.customer || item.customerData;
                title = cust ? `${cust.firstname} ${cust.lastname}` : (item.customerName || 'Unknown Customer');
                subtitle = item.quotationNumber;
                amount = item.totalAmount;
                date = item.quotationDate;
                status = item.status;
                statusClass = status === 'active' || status === 'approved' ? 'success' : 'warning';
              }

              return (
                <tr key={idx}>
                  <td>
                    <div className="fw-600 text-sm">{title}</div>
                    <div className="text-xs text-muted">{subtitle}</div>
                  </td>
                  <td><div className="fw-700 text-sm">{formatCurrency(amount)}</div></td>
                  <td><div className="text-xs">{formatDate(date)}</div></td>
                  <td><span className={`badge badge-soft-${statusClass} text-xs`}>{status?.toUpperCase()}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading && !data) return <div className="p-5 text-center"><div className="spinner-border text-primary"></div></div>;

  const { metrics } = data || {};

  const totalSubscriptions =
    (metrics?.activeSubscriptionsCount ?? 0) +
    (metrics?.expiredSubscriptionsCount ?? 0) +
    (metrics?.cancelledSubscriptionsCount ?? 0);
  const totalUsageCount =
    (metrics?.usageUsingCount ?? 0) +
    (metrics?.usageNotUsingCount ?? 0) +
    (metrics?.usageExpiredCount ?? 0);
  const totalFeedbackCount =
    (metrics?.feedbackSatisfiedCount ?? 0) +
    (metrics?.feedbackDisatisfiedCount ?? 0);
  const inactiveCustomers = Math.max(
    0,
    (metrics?.totalCustomers ?? 0) - (metrics?.activeCustomers ?? 0)
  );

  const summaryCards = [
    {
      title: 'Total Customers',
      value: metrics?.totalCustomers ?? 0,
      icon: 'people',
      theme: 'blue',
      chip: `Active ${metrics?.activeCustomers ?? 0}`,
      split: [
        { label: 'Active', value: metrics?.activeCustomers ?? 0, tone: 'success' },
        { label: 'Inactive', value: inactiveCustomers, tone: 'dark' }
      ]
    },
    {
      title: 'Subscription',
      value: totalSubscriptions,
      icon: 'receipt',
      theme: 'violet',
      chip: `Total ${totalSubscriptions}`,
      stats: [
        { label: 'Active', value: metrics?.activeSubscriptionsCount ?? 0, tone: 'success' },
        { label: 'Expired', value: metrics?.expiredSubscriptionsCount ?? 0, tone: 'warning' },
        { label: 'Canceled', value: metrics?.cancelledSubscriptionsCount ?? 0, tone: 'danger' }
      ]
    },
    {
      title: 'Subscription Usage',
      value: totalUsageCount,
      icon: 'graph-up-arrow',
      theme: 'teal',
      chip: `Total ${totalUsageCount}`,
      stats: [
        { label: 'Using', value: metrics?.usageUsingCount ?? 0, tone: 'success' },
        { label: 'Not Using', value: metrics?.usageNotUsingCount ?? 0, tone: 'primary' },
        { label: 'Expired', value: metrics?.usageExpiredCount ?? 0, tone: 'danger' }
      ]
    },
    {
      title: 'Feedback',
      value: totalFeedbackCount,
      icon: 'chat-left-text',
      theme: 'amber',
      chip: `Total ${totalFeedbackCount}`,
      stats: [
        { label: 'Satisfied', value: metrics?.feedbackSatisfiedCount ?? 0, tone: 'success' },
        { label: 'Disatisfied', value: metrics?.feedbackDisatisfiedCount ?? 0, tone: 'danger' }
      ]
    },
    {
      title: 'Total Payment Received',
      value: formatCurrency(metrics?.totalPaymentReceived ?? 0),
      icon: 'wallet2',
      theme: 'slate'
    }
  ];

  const compactCards = [
    {
      title: 'Total Customers',
      value: metrics?.totalCustomers ?? 0,
      chip: `Active ${metrics?.activeCustomers ?? 0}`,
      chipTone: 'success'
    },
    {
      title: 'Active Subscriptions',
      value: metrics?.activeSubscriptionsCount ?? 0,
      chip: `Expired ${metrics?.expiredSubscriptionsCount ?? 0}`,
      chipTone: 'primary'
    },
    {
      title: 'Quote Conversion Rate',
      value: `${metrics?.quoteConversionRateInRange ?? 0}%`,
      chip: `${metrics?.quotesConvertedInRange ?? 0}/${metrics?.quotesInRange ?? 0} converted`,
      chipTone: 'success'
    },
    {
      title: 'Renewal Rate',
      value: `${metrics?.renewalRate ?? 0}%`,
      chip: `Churn ${metrics?.churnRate ?? 0}%`,
      chipTone: 'primary'
    }
  ];

  return (
    <div className="container-fluid py-4 dashboard-page">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h4 className="fw-800 mb-0">Billing Dashboard</h4>
          <p className="text-muted small mb-0">Real-time financial overview</p>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {userRole === 'owner' && (
            <select
              className="form-select dashboard-header-select"
              value={assignTo}
              onChange={(e) => setAssignTo(e.target.value)}
            >
              <option value="">All Members</option>
              {members.map((member) => (
                <option key={member.username} value={member.username}>
                  {member.label || member.username}
                </option>
              ))}
            </select>
          )}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Button
              variant="contained"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              startIcon={<i className="bi bi-calendar3"></i>}
              className="rounded-3 shadow-sm"
            >
              {dayjs(dateRange.startDate).format('DD MMM')} - {dayjs(dateRange.endDate).format('DD MMM YYYY')}
            </Button>
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ className: 'p-3 shadow border-0 rounded-3 mt-2' }}
            >
              <Box className="d-flex flex-column gap-3">
                <h6 className="fw-700 mb-0 px-1">Select Date Range</h6>
                <div className="d-flex gap-2">
                  <DatePicker
                    label="Start Date"
                    value={dayjs(dateRange.startDate)}
                    onChange={(newValue) => setDateRange(prev => ({ ...prev, startDate: newValue.format('YYYY-MM-DD') }))}
                    slotProps={{ textField: { size: 'small' } }}
                  />
                  <DatePicker
                    label="End Date"
                    value={dayjs(dateRange.endDate)}
                    onChange={(newValue) => setDateRange(prev => ({ ...prev, endDate: newValue.format('YYYY-MM-DD') }))}
                    slotProps={{ textField: { size: 'small' } }}
                  />
                </div>
                <div className="d-flex flex-wrap gap-2 px-1">
                  {[
                    { label: 'This Month', preset: 'this_month' },
                    { label: 'Last Month', preset: 'last_month' },
                    { label: 'Last Quarter', preset: 'last_quarter' },
                    { label: 'Last 7 Days', preset: '7d' },
                    { label: 'Last 30 Days', preset: '30d' }
                  ].map(p => (
                    <Button
                      key={p.preset}
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const now = dayjs();
                        if (p.preset === 'this_month') {
                          setDateRange({ startDate: now.startOf('month').format('YYYY-MM-DD'), endDate: now.format('YYYY-MM-DD') });
                        } else if (p.preset === 'last_month') {
                          const start = now.subtract(1, 'month').startOf('month');
                          const end = now.subtract(1, 'month').endOf('month');
                          setDateRange({ startDate: start.format('YYYY-MM-DD'), endDate: end.format('YYYY-MM-DD') });
                        } else if (p.preset === 'last_quarter') {
                          const start = now.subtract(3, 'month').startOf('month');
                          const end = now.subtract(1, 'month').endOf('month');
                          setDateRange({ startDate: start.format('YYYY-MM-DD'), endDate: end.format('YYYY-MM-DD') });
                        } else if (p.preset === '7d') {
                          setDateRange({ startDate: now.subtract(7, 'day').format('YYYY-MM-DD'), endDate: now.format('YYYY-MM-DD') });
                        } else {
                          setDateRange({ startDate: now.subtract(30, 'day').format('YYYY-MM-DD'), endDate: now.format('YYYY-MM-DD') });
                        }
                        setAnchorEl(null);
                      }}
                    >
                      {p.label}
                    </Button>
                  ))}
                </div>
              </Box>
            </Popover>
          </LocalizationProvider>
        </div>
      </div>

      <div className="row g-3 g-lg-4 mb-4">
        {summaryCards.map((card) => (
          <div
            key={card.title}
            className={card.title === 'Total Payment Received' ? 'col-12 col-sm-6 col-xl-2' : 'col-12 col-sm-6 col-xl'}
          >
            <div className={`card border-0 shadow-sm h-100 dashboard-summary-card dashboard-summary-card--${card.theme}`}>
              <div className="card-body p-3 p-lg-4">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div className={`dashboard-summary-icon dashboard-summary-icon--${card.theme}`}>
                    <i className={`bi bi-${card.icon}`}></i>
                  </div>
                  {card.chip ? (
                    <span className={`dashboard-summary-chip dashboard-summary-chip--${card.theme}`}>
                      {card.chip}
                    </span>
                  ) : null}
                </div>
                <div className="dashboard-summary-title">{card.title}</div>
                <div className="dashboard-summary-value">{card.value}</div>

                {card.split ? (
                  <div className="dashboard-summary-split">
                    {card.split.map((item) => (
                      <div key={item.label} className="dashboard-summary-split-item">
                        <div className="dashboard-summary-split-label">{item.label}</div>
                        <div className={`dashboard-summary-split-value text-${item.tone}`}>{item.value}</div>
                      </div>
                    ))}
                  </div>
                ) : null}

                {card.stats ? (
                  <div className={`dashboard-summary-stats dashboard-summary-stats--${card.stats.length === 2 ? '2' : '3'}`}>
                    {card.stats.map((item) => (
                      <div key={item.label} className="dashboard-summary-stat">
                        <div className="dashboard-summary-stat-label">
                          <span className={`dashboard-summary-dot bg-${item.tone}`}></span>
                          {item.label}
                        </div>
                        <div className={`dashboard-summary-stat-value text-${item.tone}`}>{item.value}</div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-3 mb-4">
        {compactCards.map((card) => (
          <div key={card.title} className="col-12 col-sm-6 col-xl-3">
            <div className="card border-0 shadow-sm h-100 dashboard-mini-card">
              <div className="card-body p-3">
                <div className="d-flex justify-content-between align-items-start gap-2 mb-2 flex-wrap">
                  <div className="dashboard-mini-title">{card.title}</div>
                  <span className={`badge bg-soft-${card.chipTone} text-${card.chipTone} rounded-pill px-2 py-1`}>
                    {card.chip}
                  </span>
                </div>
                <div className="dashboard-mini-value">{card.value}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-4 mb-4">
        <div className="col-lg-7">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header border-0 bg-transparent pt-4 px-4 d-flex justify-content-between align-items-center">
              <div>
                <h6 className="fw-700 mb-0">Monthly Revenue Trend</h6>
                <div className="text-xs text-muted">Net collected over the last 12 months</div>
              </div>
              <div className="d-flex gap-3 small text-muted">
                <div><span className="d-inline-block me-1 text-primary fs-6">●</span>Net Revenue</div>
              </div>
            </div>
            <div className="card-body px-4 pb-4 pt-2">
              <div style={{ height: '320px' }}>
                <Line
                  data={trendChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        mode: 'index',
                        intersect: false,
                        callbacks: {
                          label: (ctx) => `Net Revenue: ${formatCurrency(ctx.parsed.y)}`
                        }
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        grid: { color: 'rgba(0,0,0,0.05)' },
                        ticks: {
                          callback: (v) => {
                            if (v >= 1000) return (v / 1000).toFixed(v < 10000 ? 1 : 0) + 'k';
                            return v;
                          }
                        }
                      },
                      x: { grid: { display: false } }
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-5">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header border-0 bg-transparent pt-4 px-4">
              <h6 className="fw-700 mb-0">Recent Activity</h6>
              <div className="text-xs text-muted">Latest subscriptions, renewals, payments &amp; quotes</div>
            </div>
            <div className="card-body px-0 pb-3 pt-2">
              <div className="px-4 mb-2">
                <div className="nav nav-pills custom-pills">
                  {['subscriptions', 'renewals', 'payments', 'quotations'].map(tab => (
                    <button
                      key={tab}
                      className={`nav-link px-3 py-2 me-2 fw-600 text-xs text-uppercase ${activeTab === tab ? 'active shadow-sm' : 'text-muted bg-light'}`}
                      onClick={() => setActiveTab(tab)}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
              </div>
              <div className="px-0">
                {renderActivityTable()}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4 mb-4">
        <div className="col-md-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header border-0 bg-transparent pt-4 px-4 d-flex justify-content-between align-items-center">
              <h6 className="fw-700 mb-0">Top Products (Active MRR)</h6>
              <span className="text-xs text-muted">Top 5 by billed value</span>
            </div>
            <div className="card-body px-0 pb-4 pt-2">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th className="ps-4">#</th>
                      <th>Product</th>
                      <th className="text-end">Active Subs</th>
                      <th className="pe-4 text-end">Total Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const items = data?.analytics?.top_products || [];
                      if (items.length === 0) {
                        return (
                          <tr><td colSpan="4" className="text-center text-muted py-4 small">No active subscriptions yet.</td></tr>
                        );
                      }
                      return items.map((p, i) => (
                        <tr key={i}>
                          <td className="ps-4"><span className="fw-700 text-muted">{i + 1}</span></td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <div className="avatar-sm bg-soft-primary rounded-2 d-flex align-items-center justify-content-center">
                                <i className="bi bi-box-seam text-primary"></i>
                              </div>
                              <div>
                                <div className="fw-700 text-sm">{p.product_name || 'Unknown Product'}</div>
                              </div>
                            </div>
                          </td>
                          <td className="text-end"><span className="badge bg-soft-info text-info rounded-pill px-3">{p.activeSubs}</span></td>
                          <td className="pe-4 text-end fw-800 text-sm">{formatCurrency(p.totalValue)}</td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header border-0 bg-transparent pt-4 px-4 d-flex justify-content-between align-items-center">
              <h6 className="fw-700 mb-0">Top Customers (Active MRR)</h6>
              <span className="text-xs text-muted">Top 5 by billed value</span>
            </div>
            <div className="card-body px-0 pb-4 pt-2">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th className="ps-4">#</th>
                      <th>Customer</th>
                      <th className="text-end">Active Subs</th>
                      <th className="pe-4 text-end">Total Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const items = data?.analytics?.top_customers || [];
                      if (items.length === 0) {
                        return (
                          <tr><td colSpan="4" className="text-center text-muted py-4 small">No active subscriptions yet.</td></tr>
                        );
                      }
                      return items.map((c, i) => (
                        <tr key={i}>
                          <td className="ps-4"><span className="fw-700 text-muted">{i + 1}</span></td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <div className="avatar-sm bg-soft-success rounded-2 d-flex align-items-center justify-content-center">
                                <i className="bi bi-person-fill text-success"></i>
                              </div>
                              <div>
                                <div className="fw-700 text-sm">{c.name}</div>
                                <div className="text-xs text-muted">
                                  {c.company || (c.email ? c.email : (c.phone || ''))}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="text-end"><span className="badge bg-soft-warning text-warning rounded-pill px-3">{c.activeSubs}</span></td>
                          <td className="pe-4 text-end fw-800 text-sm">{formatCurrency(c.totalValue)}</td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4">
        <div className="col-md-8">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header border-0 bg-transparent pt-4 px-4">
              <h6 className="fw-700 mb-0">Financials — Selected Period</h6>
              <div className="text-xs text-muted">
                From {formatDate(dateRange.startDate)} to {formatDate(dateRange.endDate)}
              </div>
            </div>
            <div className="card-body p-4">
              <div className="row g-3">
                {[
                  { label: 'Invoiced in Range', value: metrics?.invoicedInRange, color: 'primary', icon: 'file-earmark-invoice-fill', suffix: `(${metrics?.invoiceCount || 0} invoices)` },
                  { label: 'Paid (Net Collected)', value: metrics?.paidAmount, color: 'success', icon: 'wallet2' },
                  { label: 'Outstanding AR', value: metrics?.outstandingInvoices, color: 'danger', icon: 'exclamation-triangle-fill' },
                  { label: 'Collection Effectiveness', value: `${metrics?.collectionEffectiveness || 0}%`, color: 'info', icon: 'shield-check' },
                  { label: 'New Business', value: metrics?.newSales, color: 'info', icon: 'plus-circle', suffix: `(${metrics?.newSalesCount || 0} sales)` },
                  { label: 'Renewal Business', value: metrics?.renewalSales, color: 'success', icon: 'arrow-repeat', suffix: `(${metrics?.renewalCount || 0} renewals)` },
                  { label: 'Renewal Rate', value: `${metrics?.renewalRate ?? 0}%`, color: 'success', icon: 'arrow-repeat', suffix: `Churn ${metrics?.churnRate ?? 0}%` },
                  { label: 'Quote Conversion Rate', value: `${metrics?.quoteConversionRateInRange ?? 0}%`, color: 'info', icon: 'funnel-fill', suffix: `${metrics?.quotesConvertedInRange ?? 0}/${metrics?.quotesInRange ?? 0} converted` },
                ].map((kpi, i) => (
                  <div key={i} className="col-sm-6 col-lg-4">
                    <div className="p-3 bg-light rounded-3 h-100">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <div className={`p-2 rounded-2 bg-soft-${kpi.color}`}>
                          <i className={`bi bi-${kpi.icon} text-${kpi.color}`}></i>
                        </div>
                        <div className="text-xs text-muted fw-600 text-uppercase">{kpi.label}</div>
                      </div>
                      <div className="fw-800 text-dark mb-1">
                        {typeof kpi.value === 'number' && kpi.label !== 'Collection Effectiveness' ? formatCurrency(kpi.value) : kpi.value}
                      </div>
                      {kpi.suffix ? <div className="text-xs text-muted">{kpi.suffix}</div> : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header border-0 bg-transparent pt-4 px-4">
              <h6 className="fw-700 mb-0">Quotation Pipeline</h6>
              <div className="text-xs text-muted">All-time status counts</div>
            </div>
            <div className="card-body p-4 pt-2">
              <div style={{ height: '260px' }}>
                <Bar
                  data={{
                    labels: ['Active', 'Pending', 'Approved', 'Converted'],
                    datasets: [{
                      label: 'Quotations',
                      data: [
                        metrics?.activeQuotes || 0,
                        metrics?.pendingQuotes || 0,
                        metrics?.approvedQuotes || 0,
                        metrics?.convertedQuotes || 0
                      ],
                      backgroundColor: ['#0d6efd', '#ffc107', '#198754', '#0dcaf0'],
                      borderRadius: 8,
                      barThickness: 28
                    }]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: { y: { beginAtZero: true, ticks: { precision: 0 } }, x: { grid: { display: false } } }
                  }}
                />
              </div>
              <div className="mt-3 d-flex justify-content-between align-items-center text-xs px-1">
                <div className="text-muted fw-600">Total Value</div>
                <div className="fw-800 text-dark">{formatCurrency(metrics?.totalQuotesValue || 0)}</div>
              </div>
              <div className="d-flex justify-content-between align-items-center text-xs px-1 mt-1">
                <div className="text-muted fw-600">Conversion Rate</div>
                <div className="fw-800 text-success">{metrics?.quoteConversionRate || 0}%</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingDashboard;
